import React from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { 
  ArrowLeft, 
  Share2, 
  CheckCircle, 
  User, 
  ChevronRight,
  Clock
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const PaymentDetailsView = ({ bill, onBack }) => {
  const { t } = useLang();

  if (!bill) return null;

  const isPaymentIn = bill.type === 'sale';
  const title = isPaymentIn ? t(LangKeys.payment_details_title_in) : t(LangKeys.payment_details_title_out);
  const initials = bill.customer?.customerName?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';

  const getPaymentMethodText = () => {
    const pType = bill.paymentType?.toLowerCase();
    if (pType === 'cash') return t(LangKeys.cash);
    if (pType === 'online') return t(LangKeys.online);
    return bill.paymentType?.toUpperCase() || t(LangKeys.cash);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onBack} style={styles.backBtn}><ArrowLeft size={20} /></button>
          <h3 style={styles.title}>{title} #{bill.billNumber}</h3>
        </div>
      </div>

      <div style={styles.scrollContent}>
        {/* PAYMENT CARD */}
        <div style={styles.paymentCard}>
           <div style={styles.partyRow}>
              <div style={styles.avatar}>
                 {initials}
              </div>
              <div style={styles.partyInfo}>
                 <span style={styles.partyName}>{bill.customer?.customerName}</span>
                 <div style={styles.dateRow}>
                    <Clock size={12} color="#94A3B8" />
                    <span style={styles.dateText}>{formatDate(bill.date)}</span>
                 </div>
              </div>
              <div style={styles.amountCol}>
                 <span style={{...styles.amountVal, color: '#16A34A'}}>
                    {formatCurrency(bill.finalBillAmount)}
                 </span>
                 <span style={styles.paymentMethod}>
                    {getPaymentMethodText()}
                 </span>
              </div>
           </div>

           <div style={styles.divider} />

           <div style={styles.adjustedSection}>
              <span style={styles.adjustedLabel}>{t(LangKeys.payment_details_adjusted_invoices)}</span>
              <div style={styles.adjustedRow}>
                 <span style={styles.billNo}>#{bill.billNumber}</span>
                 <span style={styles.billAmount}>{formatCurrency(bill.finalBillAmount)}</span>
              </div>
           </div>
        </div>

        {/* SUCCESS STATUS */}
        <div style={styles.statusBox}>
           <CheckCircle size={48} color="#10B981" />
           <span style={styles.statusTitle}>Payment Successful</span>
           <span style={styles.statusSub}>The transaction has been recorded in the ledger.</span>
        </div>
      </div>

      <div style={styles.bottomBar}>
         <button style={styles.shareBtn}>
            <Share2 size={18} />
            <span>{t(LangKeys.share).toUpperCase()}</span>
         </button>
      </div>
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#F3F4F6' },
  header: { padding: '20px 24px', background: 'white', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  backBtn: { width: '36px', height: '36px', borderRadius: '10px', border: 'none', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '18px', fontWeight: '800', margin: 0 },
  scrollContent: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' },
  paymentCard: { background: 'white', borderRadius: '20px', border: '1px solid #E5E7EB', padding: '20px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' },
  partyRow: { display: 'flex', alignItems: 'center', gap: '14px' },
  avatar: { width: '48px', height: '48px', borderRadius: '24px', background: '#1D4ED8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '16px' },
  partyInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '2px' },
  partyName: { fontSize: '16px', fontWeight: '800', color: '#1E293B' },
  dateRow: { display: 'flex', alignItems: 'center', gap: '4px' },
  dateText: { fontSize: '12px', color: '#64748B', fontWeight: '600' },
  amountCol: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' },
  amountVal: { fontSize: '18px', fontWeight: '900' },
  paymentMethod: { fontSize: '11px', fontWeight: '800', color: '#64748B', background: '#F1F5F9', padding: '2px 8px', borderRadius: '4px' },
  divider: { height: '1px', background: '#F1F5F9', margin: '20px 0' },
  adjustedSection: { display: 'flex', flexDirection: 'column', gap: '10px' },
  adjustedLabel: { fontSize: '12px', color: '#94A3B8', fontWeight: '800', textTransform: 'uppercase' },
  adjustedRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  billNo: { fontSize: '15px', fontWeight: '700', color: '#334155' },
  billAmount: { fontSize: '15px', fontWeight: '800', color: '#1E293B' },
  statusBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px 20px', textAlign: 'center' },
  statusTitle: { fontSize: '20px', fontWeight: '900', color: '#1E293B' },
  statusSub: { fontSize: '14px', color: '#64748B', maxWidth: '240px', lineHeight: '1.5' },
  bottomBar: { padding: '16px 20px', background: 'white', borderTop: '1px solid #E5E7EB' },
  shareBtn: { width: '100%', height: '50px', borderRadius: '12px', border: 'none', background: '#1D4ED8', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }
};

export default PaymentDetailsView;
